import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import { Bar } from 'react-chartjs-2'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { useSelector } from 'react-redux'

import GraphContainer from '../GraphContainer'

import { socialReturnOnInvestmentReportSelector } from '../../../../../modules/selectors/socialReturnOnInvestment'
import { selectedPartnershipCurrency } from '../../../../../modules/selectors/partnerships'

import { getOptions } from './options'
import { insertIf } from '../../../../../helpers/common'
import { getHtmlLegendPlugin } from '../../../plugins'

const SocialValueGraph = () => {
  const { t } = useTranslation()

  const { stakeholderImpact } = useSelector(socialReturnOnInvestmentReportSelector)
  const { currency_symbol: currencySymbol } = useSelector(selectedPartnershipCurrency)

  const impactGraphLabels = useMemo(() => {
    return [
      ...insertIf(stakeholderImpact?.Players?.impact, 'Players'),
      ...insertIf(stakeholderImpact?.Attendees?.impact, 'Attendees'),
      ...insertIf(stakeholderImpact?.Viewers?.impact, 'Fans')
    ]
  }, [stakeholderImpact])

  const impactGraphDatasets = useMemo(
    () => [
      {
        label: 'Social Value',
        data: [
          ...insertIf(stakeholderImpact?.Players?.impact, stakeholderImpact?.Players?.impact),
          ...insertIf(stakeholderImpact?.Attendees?.impact, stakeholderImpact?.Attendees?.impact),
          ...insertIf(stakeholderImpact?.Viewers?.impact, stakeholderImpact?.Viewers?.impact)
        ],
        backgroundColor: '#129686',
        borderSkipped: false,
        maxBarThickness: 55,
        borderRadius: {
          topLeft: 4,
          topRight: 4,
          bottomLeft: 0,
          bottomRight: 0
        }
      }
    ],
    [stakeholderImpact]
  )

  const graphData = useMemo(
    () => ({
      labels: impactGraphLabels,
      datasets: impactGraphDatasets
    }),
    [impactGraphDatasets, impactGraphLabels]
  )

  const legendContainerId = useMemo(() => uuidv4(), [])

  const options = useMemo(
    () => getOptions({ legendContainerId, datasets: impactGraphDatasets, currencySymbol }),
    [legendContainerId, impactGraphDatasets, currencySymbol]
  )

  const plugins = useMemo(() => {
    const htmlLegendPlugin = getHtmlLegendPlugin()

    return [htmlLegendPlugin, ChartDataLabels]
  }, [])

  return (
    <GraphContainer title={t('Social Value by Beneficiary')} legendContainerId={legendContainerId}>
      <Bar type="bar" data={graphData} options={options} plugins={plugins} />
    </GraphContainer>
  )
}

export default SocialValueGraph
